import { ReactColumn, TableComponent } from '@components/table';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Card, CardContent } from '@components/ui/card';
import { cn } from '@core/utils/cn';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import React from 'react';
import { useParams } from 'react-router-dom';
import {
    CommunityBudgetCurrencySupportDto,
    CommunityBudgetUsageSupportDto
} from '@api/ChallengeClient';
import { CommunityContext } from '@src/communities/community';
import { BudgetDialog } from './budget-dialog';
import { CorrectUsageDialog } from './correct-usage-dialog';
import { DeleteBudgetDialog } from './delete-budget-dialog';
import { UserCell } from './user-cell';
import { useStore } from './store';

const CommunityBudget = () => {
    const { communityId } = useParams();
    const parsedCommunityId = parseInt(communityId);
    const {
        budgets,
        usages,
        dispatch,
        currencyCode,
        setCurrencyCode,
        correctUsage,
        changeBudget,
        deleteBudget
    } = useStore(parsedCommunityId);

    const community = React.useContext(CommunityContext);

    const [correcting, setCorrecting] =
        React.useState<CommunityBudgetUsageSupportDto | null>(null);
    const [budgetDialogOpen, setBudgetDialogOpen] = React.useState(false);
    const [editing, setEditing] = React.useState<
        CommunityBudgetCurrencySupportDto | undefined
    >(undefined);
    const [deleting, setDeleting] =
        React.useState<CommunityBudgetCurrencySupportDto | null>(null);

    const currencies = budgets.$?.currencies || [];

    const openAddBudget = () => {
        setEditing(undefined);
        setBudgetDialogOpen(true);
    };

    const columns: ReactColumn<CommunityBudgetUsageSupportDto>[] = [
        {
            Header: 'Period',
            id: 'yearMonth',
            disableSortBy: true,
            accessor: (row) => (
                <span className="text-sm">
                    {row.yearMonth} {row.currencyCode}
                </span>
            )
        },
        {
            Header: 'Value',
            id: 'value',
            disableSortBy: true,
            accessor: (row) => (
                <span
                    className={cn(
                        'text-sm font-semibold',
                        Number(row.value) < 0 && 'text-destructive'
                    )}
                >
                    {row.value}
                </span>
            )
        },
        {
            Header: 'Entry',
            id: 'entry',
            disableSortBy: true,
            accessor: (row) =>
                row.correctsUsageId ? (
                    <div className="flex flex-col items-start gap-1">
                        <Badge variant="warning">Correction</Badge>
                        <span className="text-xs text-muted-foreground">
                            corrects #{row.correctsUsageId}
                        </span>
                    </div>
                ) : (
                    <span className="text-sm">Reward</span>
                )
        },
        {
            Header: 'User',
            id: 'userId',
            disableSortBy: true,
            accessor: (row) => <UserCell userId={row.userId} />
        },
        {
            Header: 'Reason',
            id: 'comment',
            disableSortBy: true,
            accessor: (row) => (
                <span className="text-sm">{row.comment || '—'}</span>
            )
        },
        {
            Header: 'Occurred',
            id: 'occurredAt',
            accessor: (row) => (
                <span className="text-sm">
                    {new Date(row.occurredAt).toLocaleDateString()}
                </span>
            )
        }
    ];

    return (
        <>
            <div className="mb-4 flex items-center justify-between gap-4">
                <h2 className="text-lg font-semibold tracking-tight">
                    Budgets
                </h2>
                <Button onClick={openAddBudget}>
                    <Plus />
                    Add budget
                </Button>
            </div>

            {currencies.length === 0 ? (
                <p className="mb-6 text-sm text-muted-foreground">
                    This community is not enrolled for budget tracking. Add a
                    budget to start recording spend.
                </p>
            ) : (
                <div className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {currencies.map((currency) => (
                        <CurrencySummary
                            key={currency.currencyCode}
                            currency={currency}
                            selected={currencyCode === currency.currencyCode}
                            onSelect={() =>
                                setCurrencyCode(
                                    currencyCode === currency.currencyCode
                                        ? undefined
                                        : currency.currencyCode
                                )
                            }
                            onEdit={() => {
                                setEditing(currency);
                                setBudgetDialogOpen(true);
                            }}
                            onDelete={() => setDeleting(currency)}
                        />
                    ))}
                </div>
            )}

            <div className="mb-4">
                <h2 className="text-lg font-semibold tracking-tight">
                    Usage
                    {currencyCode ? ` · ${currencyCode}` : ''}
                </h2>
                <p className="text-sm text-muted-foreground">
                    Entries are never deleted or edited. A mistake is fixed by
                    appending a correction.
                </p>
            </div>
            <TableComponent<CommunityBudgetUsageSupportDto[]>
                items={usages}
                columns={columns}
                emptyText="No usage recorded for this community."
                onChangePage={(page) => dispatch({ type: 'page', page })}
                onPageSizeChange={(size) => dispatch({ type: 'size', size })}
                contextMenu={[
                    {
                        label: 'Correct',
                        action: (row) => setCorrecting(row)
                    }
                ]}
            />

            <CorrectUsageDialog
                usage={correcting}
                onClose={() => setCorrecting(null)}
                onCorrect={correctUsage}
            />

            <BudgetDialog
                open={budgetDialogOpen}
                budget={editing}
                ownedCurrencyCodes={community.ownedCurrencyCodes || []}
                onClose={() => setBudgetDialogOpen(false)}
                onSave={changeBudget}
            />

            <DeleteBudgetDialog
                budget={deleting}
                onClose={() => setDeleting(null)}
                onDelete={deleteBudget}
            />
        </>
    );
};

const CurrencySummary = ({
    currency,
    selected,
    onSelect,
    onEdit,
    onDelete
}: {
    currency: CommunityBudgetCurrencySupportDto;
    selected: boolean;
    onSelect(): void;
    onEdit(): void;
    onDelete(): void;
}) => {
    const period = currency.currentPeriod;
    const used = Number(period?.currentUsage ?? 0);
    const limit =
        Number(currency.budgetHardCap ?? currency.budgetValue) || undefined;
    const progressColor = period?.budgetHardCapReachedAt
        ? 'bg-destructive'
        : period?.budgetValueReachedAt
          ? 'bg-warning'
          : 'bg-primary';

    return (
        <Card
            role="button"
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect();
                }
            }}
            className={cn(
                'cursor-pointer transition-colors hover:bg-accent/50',
                selected && 'border-primary'
            )}
        >
            <CardContent className="flex flex-col gap-2 pt-6">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <span className="block truncate text-sm text-muted-foreground">
                            {currency.currencyCode.split('_')[0]}
                        </span>
                        {currency.currencyCode.includes('_') && (
                            <span
                                className="block truncate text-xs text-muted-foreground"
                                title={currency.currencyCode}
                            >
                                {currency.currencyCode}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        {period?.budgetHardCapReachedAt && (
                            <Badge variant="destructive">
                                Hard cap reached
                            </Badge>
                        )}
                        {!period?.budgetHardCapReachedAt &&
                            period?.budgetValueReachedAt && (
                                <Badge variant="warning">Budget reached</Badge>
                            )}
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Edit budget"
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit();
                            }}
                        >
                            <Pencil />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Remove budget"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete();
                            }}
                        >
                            <Trash2 />
                        </Button>
                    </div>
                </div>
                <span className="text-3xl font-semibold tracking-tight tabular-nums">
                    {used}
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                        of {limit ?? '∞'} used this month
                    </span>
                </span>

                {limit !== undefined && (
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                        <div
                            className={cn('h-full rounded-full', progressColor)}
                            style={{
                                width: `${Math.min(100, (used / limit) * 100)}%`
                            }}
                        />
                    </div>
                )}

                <span className="text-sm text-muted-foreground tabular-nums">
                    Budget {currency.budgetValue ?? '—'} · Hard cap{' '}
                    {currency.budgetHardCap ?? '—'}
                </span>
                <span className="text-xs text-muted-foreground">
                    Last changed{' '}
                    {new Date(currency.modifiedDate).toLocaleDateString()}
                </span>
            </CardContent>
        </Card>
    );
};

export default CommunityBudget;
